import React, { useState } from 'react';

// Types
import type { RouteComponentProps } from 'react-router';
import type { UpdateBudgetItemRequest } from '@zola/svc-web-api-ts-client';

// Utils
import { toastsActions } from '@zola-helpers/client/dist/es/redux/toasts';
import convertCurrencyFormat from '@zola-helpers/client/dist/es/util/convertCurrencyFormat';
import { getBudgetByAccountId, updateBudgetItem } from 'components/manage/budget/util/api';
import useCurrencyInput from 'components/manage/budget/util/useCurrencyInput';
import useBudgetUnsavedChanges from 'components/manage/budget/util/useBudgetUnsavedChanges';
import useBudgetSetOnSave from 'components/manage/budget/util/useBudgetSetOnSave';
import {
  mapCostToFormattedString,
  mapItemTypeToItemTypeDetails,
} from 'components/manage/budget/util/mappers';
import { useAppDispatch } from 'reducers/index';
import useEffectOnce from 'components/hooks/useEffectOnce';
import { handleBudgetRouteChange, useLeaveBudgetRoute } from 'components/manage/budget/routes';

// Components
import InputFieldV3 from '@zola/zola-ui/src/components/Form/inputV3/InputFieldV3';
import { InputFieldAddOnType } from '@zola/zola-ui/src/components/Form/InputFieldV2/InputFieldAddOn';
import { TextareaFieldV2 } from '@zola/zola-ui/src/components/Form/TextareaFieldV2';
import { DrawerTitle } from 'components/common/zolaUI/Drawer';
import BudgetPaymentReminder from 'components/manage/budget/BudgetPaymentReminder';
import SharedBudgetDrawerFooter from 'components/manage/budget/BudgetDrawer/SharedBudgetDrawerFooter';

// Context
import { useBudgetContext, getBudgetItemByUuid } from 'components/manage/budget/context';

// Styles
import {
  Container,
  EditLink,
  TitleFlex,
  ItemType,
  CostContainer,
  CostLabel,
  CostFields,
  SuggestedAllocation,
  StyledCheckboxField,
  PaidRemaining,
  PaidRemainingCurrency,
  PaymentsHeader,
  StyledP,
  AddPaymentButton,
  NotesLabel,
  Optional,
} from './BudgetItemView.styles';

type RouteParams = {
  itemUuid: string;
};

export type BudgetItemViewProps = RouteComponentProps<RouteParams, RouteParams> & {
  className?: string;
};

const BudgetItemView = ({
  className,
  params: { itemUuid },
  route,
  router: { setRouteLeaveHook },
}: BudgetItemViewProps): JSX.Element | null => {
  useLeaveBudgetRoute(route, setRouteLeaveHook);
  const dispatch = useAppDispatch();
  const { hasUnsavedChanges, setHasUnsavedChanges } = useBudgetUnsavedChanges(() => {
    handleBudgetRouteChange(`/item/${itemUuid}`);
  });

  const {
    actions: { receiveBudgetItem, receiveBudget, receiveUnsavedItem, clearUnsavedItem },
    state: {
      budget: { taxonomy_nodes = [] },
      item_types = [],
      unsavedChanges: { unsavedItems },
    },
  } = useBudgetContext();

  // Form values data initializer
  // If unsavedChanges exist for item, use those values first, otherwise get from API
  const item = unsavedItems[itemUuid] || getBudgetItemByUuid(itemUuid, taxonomy_nodes);
  const {
    currencyValue,
    rawCurrencyValue,
    setCurrencyValue,
    toggleIsEditingCurrency,
  } = useCurrencyInput(((item?.cost_cents || 0) / 100).toString());
  const [localItem, setLocalItem] = useState<Pick<UpdateBudgetItemRequest, 'note' | 'estimate'>>({
    note: item?.note || '',
    estimate: item?.estimate || false,
  });
  const taxonomyNodeUuid = item?.taxonomy_node_uuid || taxonomy_nodes[0].uuid; // required for API call
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCostChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHasUnsavedChanges(true);
    setCurrencyValue(e.target.value);
  };

  const handleEstimateChange = () => {
    setHasUnsavedChanges(true);
    setLocalItem(prevValue => ({ ...prevValue, estimate: !prevValue.estimate }));
  };

  const handleNoteChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { value } = e.target;
    setHasUnsavedChanges(true);
    setLocalItem(prevValue => ({ ...prevValue, note: value }));
  };

  const handleStashChangesAndChangeRoute = (path: string) => {
    if (hasUnsavedChanges) {
      // stash changes for when user comes back
      receiveUnsavedItem(itemUuid, {
        ...localItem,
        cost_cents: parseFloat(rawCurrencyValue) * 100,
      });
    }
    handleBudgetRouteChange(path, false);
  };

  const handleCancel = () => {
    handleBudgetRouteChange();
  };

  const handleSave = () => {
    const { note, estimate } = localItem;
    const request = {
      item_uuid: itemUuid,
      taxonomy_node_uuid: taxonomyNodeUuid, // required for API call
      cost_cents: parseFloat(rawCurrencyValue) * 100,
      note,
      estimate,
    };

    setHasUnsavedChanges(false);
    setIsSubmitting(true);
    updateBudgetItem(request)
      .then(res => {
        receiveBudgetItem(res);
        setLocalItem(prevValue => ({
          ...prevValue,
          cost_cents: (res.cost_cents || 0) / 100,
          estimate: res.estimate,
          note: res.note,
        }));
        dispatch(
          toastsActions.positive({
            headline: 'Updated',
          })
        );
        handleBudgetRouteChange('', false);
      })
      // update the budget summary
      .then(getBudgetByAccountId)
      .then(res => {
        receiveBudget(res);
      })
      .catch(() => {
        setHasUnsavedChanges(true);
        setIsSubmitting(false);
        dispatch(
          toastsActions.negative({
            headline: 'There was an error updating your budget item.',
          })
        );
      });
  };

  useEffectOnce(() => {
    if (unsavedItems[itemUuid]) {
      setHasUnsavedChanges(true);
      // component was initialized with any unsaved changes, so clear them on mount
      clearUnsavedItem(itemUuid);
    }
  });

  useBudgetSetOnSave(handleSave, [localItem, rawCurrencyValue]);

  if (!item) {
    dispatch(toastsActions.negative({ headline: `Budget item ${itemUuid} not found.` }));
    handleBudgetRouteChange();
    return null;
  }

  const { estimate, note } = localItem;
  const {
    title,
    item_type,
    suggested_allocation,
    paid_cents = 0,
    balance_due_cents = 0,
    payments,
  } = item;
  const itemType = mapItemTypeToItemTypeDetails((item_type as unknown) as string, item_types);

  return (
    <Container data-testid="BudgetItemView" className={className}>
      {itemType?.display_name && itemType?.vendor_type !== 'OTHER' && (
        <ItemType>{itemType?.display_name}</ItemType>
      )}
      <DrawerTitle>
        <TitleFlex>
          {title}
          <EditLink
            role="button"
            sizes="small"
            onClick={() => handleStashChangesAndChangeRoute(`/item/${itemUuid}/edit`)}
          >
            Edit
          </EditLink>
        </TitleFlex>
      </DrawerTitle>
      <CostContainer>
        <div>
          <CostLabel htmlFor="cost_cents">Cost</CostLabel>
          {suggested_allocation && (
            <SuggestedAllocation>
              Experts recommend spending {suggested_allocation.display_percentage} of your budget
              {suggested_allocation.allocated_cost_cents &&
                `, or ${mapCostToFormattedString(suggested_allocation.allocated_cost_cents)}`}
              .
            </SuggestedAllocation>
          )}
        </div>
        <CostFields>
          <InputFieldV3
            name="cost_cents"
            id="cost_cents"
            onChange={handleCostChange}
            onFocus={toggleIsEditingCurrency}
            onBlur={toggleIsEditingCurrency}
            addOn={InputFieldAddOnType.CASH}
            value={currencyValue}
            isControlled
          />
          <StyledCheckboxField
            name="estimate"
            label="This is an estimate"
            checked={estimate}
            onChange={handleEstimateChange}
          />
        </CostFields>
      </CostContainer>
      <PaidRemaining>
        <span>Paid:</span>
        <PaidRemainingCurrency>
          {convertCurrencyFormat(paid_cents / 100, 'USD', 2)}
        </PaidRemainingCurrency>
        <span>Remaining due:</span>
        <PaidRemainingCurrency>
          {convertCurrencyFormat(balance_due_cents / 100, 'USD', 2)}
        </PaidRemainingCurrency>
      </PaidRemaining>
      <PaymentsHeader>Payments and reminders</PaymentsHeader>
      {payments?.length ? (
        payments.map(payment => (
          <BudgetPaymentReminder key={payment.uuid} payment={payment} extended />
        ))
      ) : (
        <StyledP>Track your payments and get alerts on deadlines.</StyledP>
      )}
      <AddPaymentButton
        variant="secondary"
        onClick={() => handleStashChangesAndChangeRoute(`/item/${itemUuid}/payment/new`)}
      >
        Add a payment or reminder
      </AddPaymentButton>
      <NotesLabel htmlFor="note">
        Notes <Optional>(optional)</Optional>
      </NotesLabel>
      <TextareaFieldV2
        name="note"
        id="note"
        onChange={handleNoteChange}
        value={note}
        placeholder="Use this space to note deposits, related contact info, cancellation deadlines, etc."
        maxChars={200}
      />
      <SharedBudgetDrawerFooter
        handleCancel={handleCancel}
        handleSave={handleSave}
        cancelDisabled={isSubmitting}
        saveDisabled={(note && note.length > 200) || isSubmitting}
      />
    </Container>
  );
};

export default BudgetItemView;
